import {
  DashboardState,
  ECodeGenerationType,
  EProjectPublishMethod,
  EProjectType,
  ESdChangeReason,
  EUserAccessPart,
  IConfig
} from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import _ from "lodash";
import { ReactNode, createRef } from "react";
import { connect } from "react-redux";
import { CloudService } from "../../../../../services/cloud-service";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../../shared/hoc/with-context";
import { IPureDesignComponentProps, IPureDesignComponentState, PureSdComponent } from "../../../../../shared/pure-sd";
import { resolveLoadingPresentation } from "../../../../../shared/utility/first-load-skeletons";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { showDomainAccessRequiredModal } from "../../../../../store/domain-access-modal/actions";
import { setNextCallbackFunction } from "../../../../../store/unsaved-changes-modal/actions";
import ExecutablesMailSvg from "../../../../../svgs/executables-mail.svg";
import KuikaBadge from "../../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaCheckboxGroup from "../../../../ui-kit/kuika-checkbox-group/kuika-checkbox-group";
import KuikaInfoBox from "../../../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaPopover from "../../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { KuikaSkeletonMenu, KuikaSkeletonRegion } from "../../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../../ui-kit/kuika-switch/kuika-switch";
import KuikaTabContent from "../../../../ui-kit/kuika-tab-content/kuika-tab-content";
import KuikaTabs from "../../../../ui-kit/kuika-tabs/kuika-tabs";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaH6 from "../../../../ui-kit/typography/H6/H6";
import KuikaH7 from "../../../../ui-kit/typography/H7/H7";
import KuikaSmallMediumText from "../../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../../../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../../../ui-kit/typography/Text/Text";
import KuikaTextMedium from "../../../../ui-kit/typography/TextMedium/TextMedium";
import "./publish-popover.scss";

/** The popover's two tabs in their on-screen order, so a pane slides in from the side it lies on. */
const PUBLISH_POPOVER_TABS: string[] = ["Publish", "Preview"];

interface CheckboxOption {
  label: string;
  value: string;
  disabled?: boolean;
}
export interface IPublishPopupButtonState {
  selectedConfig?: IConfig;
  activeTab: string;
  selectedPublishTypes: string[];
}

export interface IPublishPopupButtonProps {
  showDomainAccessRequiredModal: (actionName: string | string[]) => void;
  setNextCallbackFunction: (cb?: () => void) => void;
  showPopup: boolean;
  setShowPopup: (showPopup: boolean) => void;
  children?: ReactNode;
  handlePreviewButtonClick: () => void;
  previewStatus: "idle" | "loading" | "success" | "error" | "warning";
  setPreviewStatus: (
    status: "idle" | "loading" | "success" | "error" | "warning",
    source?: "preview" | "export" | "source-code"
  ) => void;
  loadingSource: "preview" | "export" | "source-code" | null;
}

class PublishButton extends PureSdComponent<
  IPublishPopupButtonProps & IWithSDProps & IWithContextProp,
  IPublishPopupButtonState
> {
  formRef = createRef<FormInstance>();

  constructor(props: IPublishPopupButtonProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      selectedConfig: props.sd.getSelectedConfiguration(),
      activeTab: "Publish",
      selectedPublishTypes: []
    };
  }

  componentDidUpdate(
    prevProps: Readonly<IPureDesignComponentProps & IWithSDProps & IPublishPopupButtonProps & IWithContextProp>,
    prevState: Readonly<IPureDesignComponentState & IPublishPopupButtonState>,
    snapshot?: any
  ): void {
    // Rising edge only — re-asserting open on EVERY update fights any other surface (it
    // reopened the popover, and stole focus, each time the user clicked elsewhere — KOA-18257).
    if (this.props.sd.showExecutablesSentMessage && !prevProps.sd.showExecutablesSentMessage) {
      this.props.setShowPopup(true);
    }
    if (!this.props.sd.isPublishPopupLoading && this.props.showPopup) {
      this.unselectDisabledConfigs();
    }
    if (prevProps.showPopup !== this.props.showPopup && this.props.showPopup) {
      this.setState({ activeTab: "Publish" });
    }
  }

  getActionButtonLabel = (): string => {
    if (this.state.activeTab === "Preview") return "RUN";
    if (this.state.selectedConfig?.isKuikaCloud) return "PUBLISH";
    return "EXPORT";
  };

  getTooltipContent = (status: string, source: "preview" | "export" | "source-code" | null): string => {
    // Determine the operation label based on source
    const operationLabel = source === "source-code" ? "Getting Source Code" : "Export";

    switch (status) {
      case "loading":
        return `${operationLabel} In Progress`;
      case "success":
        return `${operationLabel} Completed`;
      case "error":
        return `${operationLabel} Failed`;
      case "warning":
        return `${operationLabel} Busy`;
      default:
        return "Run Settings";
    }
  };

  getTooltipColor = (status: string): "green" | "red" | "yellow" | null => {
    switch (status) {
      case "success":
        return "green";
      case "error":
        return "red";
      case "warning":
        return "yellow";
      default:
        return null;
    }
  };

  handleRedirectToRunSettings = () => {
    this.props.setShowPopup(false);
    if (this.props.sd.dashboardState === DashboardState.runSettings) return;

    const redirectToRunSettings = () => {
      this.props.sd.setDashboardState(DashboardState.runSettings);

      this.props.sd.setCreateNotificationTemplateActive();
      this.props.sd.clearNotificationTemplatePanel();
      this.props.sd.cancelEditingScheduledJob();
      this.props.sd.cancelEditingApi();
      this.props.sd.cancelActiveMethod();
      this.props.sd.cancelCreatingOrEditingDatasource();
      this.props.sd.cancelCreatingOrEditingConfiguration();
      this.props.sd.clearSelectedWf();
    };

    if (
      this.props.sd?.activeConfiguration ||
      this.props.sd?.activeEditingDatasource ||
      this.props.sd?.activeCreatingDatasourceType !== undefined ||
      this.props.sd?.editingTable ||
      this.props.sd?.editingAction ||
      this.props.sd?.activeCreatingTableName ||
      this.props.sd?.activeCreatingActionName ||
      this.props.sd?.editingNotificationTemplate ||
      this.props.sd?.getSelectedApi()
    ) {
      this.props.setNextCallbackFunction(redirectToRunSettings);
      return;
    }

    redirectToRunSettings();
  };

  handleSelectConfig = (value: any) => {
    this.updateSelectedConfig(this.props.sd.configurations.find((conf) => value === conf.id));
  };

  handleVisibilityChange = (visible: boolean) => {
    if (!visible) {
      this.formRef.current?.resetFields();
      this.props.setShowPopup(false);
      this.setState({ selectedConfig: undefined, selectedPublishTypes: [] });
      return;
    }

    const fetchConfigCallback = () => {
      this.updateSelectedConfig(this.props.sd.getSelectedConfiguration());
    };

    if (this.props.sd.dashboardState === DashboardState.configManager) {
      this.props.sd.fetchConfigs({
        shouldLockPage: true,
        shouldFetchGlobals: true,
        callback: () => fetchConfigCallback
      });
    } else {
      this.props.sd.fetchLightweightConfigs({
        shouldLockPublishPopup: true,
        callback: () => fetchConfigCallback
      });
    }
  };

  updateSelectedConfig = (selectedConfig?: IConfig) => {
    this.setState({ selectedConfig }, () => {
      if (selectedConfig) {
        let selectedPublishTypes = selectedConfig.publishMethods?.split(",");
        const hasWorkflow = this.props.sd.workFlowList.filter((w) => !w.inactived).length > 0;
        if (hasWorkflow) {
          if (!selectedPublishTypes) selectedPublishTypes = [];
          if (!selectedPublishTypes?.includes("6")) selectedPublishTypes?.push("6");
        }
        this.setState({ selectedPublishTypes: selectedPublishTypes || [] }, () => {
          this.formRef.current?.setFieldsValue({
            selectedConfigID: selectedConfig.id,
            selectedPublishTypes: this.state.selectedPublishTypes,
            backup: false
          });
          this.unselectDisabledConfigs(this.state.selectedPublishTypes);
        });
        return;
      }

      this.setState({ selectedPublishTypes: [] }, () => {
        this.formRef.current?.setFieldsValue({
          selectedConfigID: undefined,
          selectedPublishTypes: [],
          backup: false
        });
      });
    });
  };

  gotoSelectedConfig = (isNewConfig?: boolean) => {
    const { selectedConfig } = this.state;

    if (!selectedConfig && !isNewConfig) return;
    if (
      this.props.sd.dashboardState === DashboardState.configManager &&
      this.props.sd.activeConfiguration?.id === selectedConfig?.id &&
      !isNewConfig
    )
      return;

    const redirectToConfigurationManager = () => {
      if (this.props.sd.dashboardState !== DashboardState.configManager)
        this.props.sd.setDashboardState(DashboardState.configManager);

      this.props.sd.cancelCreatingOrEditingConfiguration();

      this.props.sd.setCreateNotificationTemplateActive();
      this.props.sd.clearNotificationTemplatePanel();
      this.props.sd.cancelEditingScheduledJob();
      this.props.sd.cancelEditingApi();
      this.props.sd.cancelActiveMethod();
      this.props.sd.cancelCreatingOrEditingDatasource();
      this.props.sd.clearSelectedWf();
      if (!isNewConfig && selectedConfig) {
        this.props.sd.fetchConfigs({
          shouldLockPage: true,
          shouldFetchGlobals: true,
          callback: () => {
            const conf = _.cloneDeep(this.props.sd.configurations.find((x) => x.id === selectedConfig.id));
            if (conf) {
              this.props.sd.setActiveConfiguration(conf);
              return;
            }

            KMainFunctions.displayWarningNotification(
              `Your configuration named [${selectedConfig.name}] is somehow not found. Please contact to the customer service.`
            );
          }
        });
      }

      this.props.setShowPopup(false);
      this.setState({ selectedConfig: undefined });
    };

    if (
      this.props.sd?.activeConfiguration ||
      this.props.sd?.activeEditingDatasource ||
      this.props.sd?.activeCreatingDatasourceType !== undefined ||
      this.props.sd?.editingTable ||
      this.props.sd?.editingAction ||
      this.props.sd?.activeCreatingTableName ||
      this.props.sd?.activeCreatingActionName ||
      this.props.sd?.editingNotificationTemplate ||
      this.props.sd?.getSelectedApi() ||
      this.props.sd.dashboardState === DashboardState.runSettings
    ) {
      this.props.setNextCallbackFunction(redirectToConfigurationManager);
      return;
    }

    redirectToConfigurationManager();
  };

  unselectDisabledConfigs = (selectedPublishTypesParam?: string[]) => {
    const selectedConfig = this.state.selectedConfig;
    let selectedPublishTypes = selectedPublishTypesParam || this.state.selectedPublishTypes;

    if (selectedConfig && selectedPublishTypes) {
      if (selectedConfig.supportAndroid === false) {
        selectedPublishTypes = selectedPublishTypes.filter((type) => type !== `${EProjectPublishMethod.android}`);
      }
      if (selectedConfig.supportIOS === false) {
        selectedPublishTypes = selectedPublishTypes.filter((type) => type !== `${EProjectPublishMethod.ios}`);
      }
      if (selectedConfig.supportWeb === false && this.props.sd.workFlowList.length === 0) {
        selectedPublishTypes = selectedPublishTypes.filter((type) => type !== `${EProjectPublishMethod.webUi}`);
      }
      if (selectedConfig.supportElectron === false) {
        selectedPublishTypes = selectedPublishTypes.filter((type) => type !== `${EProjectPublishMethod.electron}`);
      }
      this.setState({ selectedPublishTypes }, () => {
        this.formRef.current?.setFieldsValue({
          selectedPublishTypes
        });
      });
    }
  };

  platformCheckBoxGroupContainer = (): ReactNode => {
    const { selectedConfig } = this.state;

    if (!selectedConfig) return <></>;

    const hasActiveWF = this.props.sd.workFlowList.filter((w) => !w.inactived).length > 0;

    const isMainAi = KMainFunctions.isMainAI();

    const publishOptions = [
      {
        label: "Web",
        value: `${EProjectPublishMethod.webUi}`,
        disabled: !selectedConfig.supportWeb || hasActiveWF,
        description: isMainAi
          ? "Publish and test a Web application you have developed in Main AI. "
          : "Publish and test a Web application you have developed in Kuika. "
      }
    ];
    if (this.props.sd.project.projectType === EProjectType.Mobile) {
      publishOptions.push({
        label: "Android",
        value: `${EProjectPublishMethod.android}`,
        disabled: !selectedConfig.supportAndroid,
        description: isMainAi
          ? "Publish a mobile application you have developed in Main AI and test it on Appetize or Android Studio."
          : "Publish a mobile application you have developed in Kuika and test it on Appetize or Android Studio."
      });
      publishOptions.push({
        label: "iOS",
        value: `${EProjectPublishMethod.ios}`,
        disabled: !selectedConfig.supportIOS,
        description: isMainAi
          ? "Publish a mobile application you developed in Main AI and test it on Testflight."
          : "Publish a mobile application you developed in Kuika and test it on Testflight."
      });
    }

    if (selectedConfig.supportElectron) {
      publishOptions.push({
        label: "Desktop App",
        value: `${EProjectPublishMethod.electron}`,
        disabled: false,
        description: "Publish the app as a desktop application for Windows, macOS, and Linux."
      });
    }

    // React Native is no longer a publish target of its own: Android and iOS are the
    // mobile targets, and App Settings → MOBILE GENERATION TYPE decides whether they
    // are generated as native projects or as one React Native (Expo) project.

    const isAllOptionsDisabled = hasActiveWF
      ? false
      : publishOptions.filter((option) => option.disabled === true).length === publishOptions.length;

    return (
      <>
        {hasActiveWF && (
          <>
            <KuikaBadge
              content="Web is auto selected as you have active Process."
              leftIcon={<KuikaSvg name="info-circle" width={16} height={16} />}
              color="yellow"
            ></KuikaBadge>
          </>
        )}
        <Form.Item
          name="selectedPublishTypes"
          noStyle
          rules={[
            {
              validator: async (_rule, _value) => {
                if (isAllOptionsDisabled)
                  throw new Error(
                    "All platforms are disabled, please enable platform(s) that you want to publish or download source code."
                  );
              }
            },
            {
              required: !isAllOptionsDisabled,
              message: "Please select a platform to publish"
            }
          ]}
        >
          <KuikaCheckboxGroup
            options={publishOptions}
            onChange={(value) => {
              this.setState({ selectedPublishTypes: value });
            }}
          />
        </Form.Item>
      </>
    );
  };

  handleDownloadSourceCode = () => {
    const allowedDomains = (window as any)._env_?.CODE_EXPORT_LICENSE?.split(",").map((e) => e.trim()) || [
      "b07bccfe-7df2-49ca-8f34-94d85c92e5f5",
      "34f43741-9c8b-43d4-9973-78a717aae456",
      "996d2b06-ff04-4bac-a664-71fc3ecddc72", // this one is for onprem ISS domain "ISSPestControl"
      "46dbf3e9-88e4-49a0-9bff-290a61006253", // HVLSN
      "f06a44cc-ffe4-444b-b6d7-4fc343cd43d8", // hvlsn ant başkanlığı
      "d8c6389a-4afb-48cd-9434-1307bd448a55", // roz_01 hvlsn
      "9b013ec0-6d2e-464d-a997-276bb60133b2", // aaydinbas_01 havlsn
      "6d62fcac-6c5a-4651-ac13-3733763f8c26", // esafak_01 hvlsn
      "e5f7ca4b-fe49-4f83-a897-53ee94e39eea", // hvlsn egitim
      "fa711d65-14f8-49f0-b19c-605c0107dc55", // hvlsn burakgulbay_01
      "193f4edc-6176-46c8-9e5d-e5f28024da8f", // hvlsn ebsahin_01
      "4bad6f75-4c22-493a-9104-bd435273ad6b", // Reslen_01
      "d340f3a4-b273-4da6-8928-21d70d6f6175", // Ermetal
      "2f1d2f7b-fd53-4433-b9ef-74db2926b8f2", // AksaDemo
      "21311719-d805-465d-9c47-ce9a30d07961", // ozanselvi_01
      "d4bf9457-1fb6-4807-8d2a-321046f7e117" // bakioglu
    ];

    const isMainAiUser =
      this.props.context?.userInfo?.username && this.props.context?.userInfo?.username.includes("mainlowcode@");

    if (allowedDomains.includes(this.props.sd.getProject().domainId) || isMainAiUser) {
      this.downloadSourceCode();
    } else if (KMainFunctions.isUserKuikaDeveloper(this.props.context?.userInfo?.username) === false) {
      this.props.showDomainAccessRequiredModal("Code Export");
      return;
    } else {
      this.downloadSourceCode();
    }
  };

  downloadSourceCode = () => {
    this.formRef.current
      ?.validateFields()
      .then(({ selectedPublishTypes, backup }) => {
        this.props.setPreviewStatus?.("loading");
        this.setCodeGenerationTypeAndPublish(ECodeGenerationType.DownloadSourceCode, selectedPublishTypes, backup);
      })
      .catch((_reason) => {});
  };

  handlePublish = async () => {
    try {
      const { selectedPublishTypes } = await this.formRef.current?.validateFields();

      const planName: string | undefined = this.props.context?.userInfo?.currentPlanName?.toLowerCase();
      const shouldUseCloudDeployment: boolean =
        CloudService.GetIsCloudEnabled() && !!planName && planName !== "" && !!this.state.selectedConfig?.isKuikaCloud;

      this.setCodeGenerationTypeAndPublish(
        ECodeGenerationType.Publish,
        selectedPublishTypes,
        undefined,
        shouldUseCloudDeployment,
        // Internal Kuika Developer Publish: emit the DbMigrationController and
        // MigrationDbScript*.sql files even though this is a Publish. This path is only
        // reachable from the internal-developer control (see getPopupContent), which renders
        // solely for isUserKuikaDeveloper users, so the flag stays scoped to internal devs.
        true
      );
    } catch (_reason) {
      // Validation failed
    }
  };

  handleSubmit = async (data) => {
    const { backup } = data;

    const planName: string | undefined = this.props.context?.userInfo?.currentPlanName?.toLowerCase();
    const shouldUseCloudDeployment: boolean = !!planName && planName !== "";

    // Check if cloud deployment should be triggered after successful export
    const shouldTriggerCloudDeployment =
      shouldUseCloudDeployment && this.state.activeTab === "Publish" && !!this.state.selectedConfig?.isKuikaCloud;

    // Do the default export with cloud deployment flag
    this.setCodeGenerationTypeAndPublish(
      ECodeGenerationType.DownloadExecutables,
      this.state.selectedPublishTypes,
      backup,
      shouldTriggerCloudDeployment
    );
  };

  setCodeGenerationTypeAndPublish = async (
    type: ECodeGenerationType,
    selectedPublishTypes: string[],
    backup?: boolean,
    shouldTriggerCloudDeployment?: boolean,
    includeMigrationArtifactsOnPublish?: boolean
  ) => {
    if (!this.state.selectedConfig) return;

    // Set the loading source based on the operation type
    const source = type === ECodeGenerationType.DownloadSourceCode ? "source-code" : "export";
    this.props.setPreviewStatus("loading", source);

    if (selectedPublishTypes.includes(`${EProjectPublishMethod.android}`)) {
      if (KMainFunctions.isDomainHasAccess("Android") === false) {
        this.props.showDomainAccessRequiredModal("Android");
        return;
      }
    }

    if (selectedPublishTypes.includes(`${EProjectPublishMethod.ios}`)) {
      if (KMainFunctions.isDomainHasAccess("IOS") === false) {
        this.props.showDomainAccessRequiredModal("IOS");
        return;
      }
    }

    await this.props.sd.setCodeGenerationTypeAndPublish(
      type,
      this.state.selectedConfig,
      selectedPublishTypes.toString(),
      backup,
      shouldTriggerCloudDeployment,
      includeMigrationArtifactsOnPublish
    );
    this.props.setShowPopup(false);
  };

  executablesHasSentContent = () => {
    // The timer ticks once a second and the bar fills in about 33 of them; clamped so a
    // long generation does not overrun its own track.
    const progressPercent: number = Math.min(100, Math.max(0, this.props.sd.generationProcessTimer * 3));
    return (
      <div data-testid="successPublishModal" className="kuika_executables-sent__content">
        <ExecutablesMailSvg />
        <KuikaText>
          We took flight. We will inform via your {this.props.context?.userInfo?.username} email address when we arrive
          at the destination.
        </KuikaText>
        <KuikaButton
          data-testid="successPublishModalCloseButton"
          data-onboarding-id="publish-popover-executable-sent-close-button"
          type="text"
          size="sm"
          onClick={() => {
            this.props.sd.handleExecutablesSentMessageVisibility(false);
            this.props.setShowPopup(false);
          }}
        >
          Close
        </KuikaButton>
        {/* How far along the generation is. antd's `Progress` drew the track in a
          light-theme literal and shipped a ring, a dashboard and a gradient nobody here
          asked for; a determinate bar is two elements. */}
        <div
          className="kuika-publish-popover__progress"
          role="progressbar"
          aria-label="Generation progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progressPercent}
        >
          <span className="kuika-publish-popover__progress-fill" style={{ width: `${progressPercent}%` }} />
        </div>
      </div>
    );
  };

  getPopupTitle = (): ReactNode => {
    if (this.props.sd.showExecutablesSentMessage) return;
    return "Generate App";
  };

  isOnNonMasterBranch = (): boolean => {
    const currentBranch = this.props.sd.currentBranch;
    if (!currentBranch) return false;
    return currentBranch.name?.toLowerCase() !== "master";
  };

  getPopupContent = (): ReactNode => {
    if (this.props.sd.showExecutablesSentMessage) return this.executablesHasSentContent();
    const hasUserAccessDownloadSourceCode = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.DownloadSourceCodeOfProject,
      this.props.sd.project?.projectAccess
    );
    const isMainAi = KMainFunctions.isMainAI();
    const isExportBlockedByBranch = this.isOnNonMasterBranch();
    // The configurations are fetched as the popover opens. Until the first answer there is nothing
    // to list, so the list is drawn in outline; a re-fetch keeps the list that is already there. It
    // used to dim the whole popover — the Preview tab included, which needs no configuration.
    const isConfigListPlaceholder: boolean =
      resolveLoadingPresentation(
        !!this.props.sd.isPublishPopupLoading,
        (this.props.sd.configurations?.length ?? 0) > 0
      ) === "skeleton";
    return (
      <Form ref={this.formRef} onFinish={this.handleSubmit}>
        <div className="kuika-publish-popover__content">
          <div className="kuika-publish-popover__tabs">
            <KuikaTabs
              options={PUBLISH_POPOVER_TABS}
              onChange={(value) => this.setState({ activeTab: value })}
              data-onboarding-id="kuika-publish-popover_tabs"
            />
          </div>
          <KuikaTabContent activeKey={this.state.activeTab} order={PUBLISH_POPOVER_TABS}>
            <div key="Preview" className="kuika-publish-popover__preview-area">
              <div className="kuika-flex-column--gap">
                <div style={{ lineHeight: "17px" }}>
                  <KuikaSmallText data-testid="previewDescription" className="preview-text">
                    Preview your application in a new browser tab.
                  </KuikaSmallText>
                  {/* <KuikaSmallText className="preview-text--link"> Learn more...</KuikaSmallText> */}
                </div>
              </div>
              <div className="kuika-flex--gap-xs">
                <KuikaButton
                  data-testid="lastPreviewIcon"
                  data-onboarding-id="publish-popover-last-preview-button"
                  type="tertiary"
                  size="sm"
                  isIcon
                  onClick={() => {
                    window.open(KMainFunctions.constructGeneratedFrontendUrl(this.props.sd), "_blank");
                  }}
                >
                  <KuikaSvg name="external-link" width={16} height={16} />
                </KuikaButton>
                <KuikaButton
                  data-testid="previewSettingsIcon"
                  data-onboarding-id="publish-popover-preview-settings-button"
                  type="tertiary"
                  color="neutral"
                  size="sm"
                  isIcon
                  onClick={this.handleRedirectToRunSettings}
                >
                  <KuikaSvg name="settings" type="sidebar-icons" width={16} height={16} />
                </KuikaButton>
              </div>
            </div>
            <div key="Publish" className="kuika-publish-popover__pane">
              {isConfigListPlaceholder && (
                <KuikaSkeletonRegion label="Loading configurations" className="kuika-publish-popover__config-skeleton">
                  <KuikaSkeletonMenu rows={3} />
                </KuikaSkeletonRegion>
              )}
              {!isConfigListPlaceholder &&
                (!this.props.sd.configurations ||
                this.props.sd.configurations.filter((configuration) => !configuration.isRunConfig).length === 0 ? (
                  <div className="kuika-publish-popover__no-config">
                    <div className="kuika-publish-popover__no-config__icon">
                      <KuikaSvg name="devices-cog" width={24} height={24} pointer={false} />
                    </div>
                    <div className="kuika-flex-column--gap-m">
                      <KuikaH6 data-testid="newConfigurationTitle">Looks like you need a configuration!</KuikaH6>
                      <KuikaSmallText data-testid="newConfigurationDescription">
                        Let's get you set up! Create your first config to begin the publishing adventure.
                      </KuikaSmallText>
                    </div>
                    <KuikaButton
                      data-testid="newConfigurationButton"
                      data-onboarding-id="publish-popover-new-configuration-button"
                      type="secondary"
                      size="sm"
                      uppercase
                      onClick={() => this.gotoSelectedConfig(true)}
                    >
                      <KuikaSvg name="plus" width={16} height={16} />
                      <KuikaSmallMediumText>New Configuration</KuikaSmallMediumText>
                    </KuikaButton>
                  </div>
                ) : (
                  <>
                    <div className="kuika-publish-popover__config-list">
                      <div className="kuika-publish-popover__config-select">
                        <div
                          className="kuika-flex--gap kuika-align-center"
                          data-testid="choosePublishConfigurationSelectbox"
                        >
                          <Form.Item
                            name="selectedConfigID"
                            rules={[
                              {
                                required: true,
                                message: "Please select a configuration to publish"
                              }
                            ]}
                            noStyle
                          >
                            <KuikaSelectbox
                              placeholder="Choose publish configuration"
                              onChange={this.handleSelectConfig}
                              onClear={() => this.handleSelectConfig("")}
                              size="lg"
                              allowClear
                              options={this.props.sd.configurations
                                .filter((config) => !config.isRunConfig && !config.isGlobalConfigOnProjectConfigList)
                                .map((config) => ({
                                  value: config.id,
                                  label: config.name
                                }))}
                            />
                          </Form.Item>
                          <KuikaButton
                            data-testid="editSelectedConfigurationButton"
                            data-onboarding-id="publish-popover-edit-configuration-button"
                            type="tertiary"
                            color="neutral"
                            size="sm"
                            isIcon
                            onClick={() => this.gotoSelectedConfig()}
                            disabled={!this.state.selectedConfig && this.state.activeTab === "Publish"}
                          >
                            <KuikaSvg name="settings" type="sidebar-icons" width={16} height={16} />
                          </KuikaButton>
                        </div>
                      </div>
                      {this.platformCheckBoxGroupContainer()}
                    </div>
                    {this.state.selectedConfig && (
                      <div className="kuika-publish-popover__backup-area">
                        <KuikaH7>Backup & Archive</KuikaH7>
                        <Form.Item name="backup" noStyle valuePropName="checked">
                          <KuikaSwitch data-testid="backupAndArchiveSwitch" />
                        </Form.Item>
                      </div>
                    )}
                  </>
                ))}
            </div>
          </KuikaTabContent>
          {isExportBlockedByBranch && this.state.activeTab === "Publish" && (
            <div className="kuika-publish-popover__branch-notice">
              <KuikaInfoBox
                color="yellow"
                leftUnit={<KuikaSvg type="common" name="circle-warning" width={16} height={16} />}
                rightUnit={
                  <KuikaSmallText>
                    Export is only available from the master branch. Switch to master to export your project.
                  </KuikaSmallText>
                }
              />
            </div>
          )}
          <div className="kuika-publish-popover__footer">
            {this.state.activeTab === "Publish" ? (
              <div className="kuika-flex-center--gap" style={{ cursor: "pointer" }}>
                {KMainFunctions.isUserKuikaDeveloper(this.props?.context.userInfo?.username) && (
                  <KuikaTooltip content="Internal Kuika Developer Publish">
                    {isMainAi ? (
                      this.state.selectedConfig ? (
                        <img
                          src="/assets/main-ai/favicon_32x32.svg"
                          style={{ width: 24, height: 24 }}
                          alt="main-ai-logo"
                        />
                      ) : (
                        <img src="/assets/main-ai/action.svg" style={{ width: 24, height: 24 }} alt="main-ai-logo" />
                      )
                    ) : !this.state.selectedConfig ? (
                      <img src="/images/common/kuika-logo-passive.svg" alt="kuika-logo" />
                    ) : (
                      <img onClick={this.handlePublish} src="/images/common/kuika-logo.svg" alt="kuika-logo" />
                    )}
                  </KuikaTooltip>
                )}

                {hasUserAccessDownloadSourceCode && (
                  <KuikaTooltip
                    content={isExportBlockedByBranch ? "Export is only available from the master branch" : ""}
                  >
                    <KuikaButton
                      data-testid="getSourceCodeButton"
                      data-onboarding-id="publish-popover-get-source-code-button"
                      type="text"
                      onClick={this.handleDownloadSourceCode}
                      disabled={!this.state.selectedConfig || isExportBlockedByBranch}
                      style={{ padding: 0 }}
                    >
                      <KuikaTextMedium>GET SOURCE CODE</KuikaTextMedium>
                    </KuikaButton>
                  </KuikaTooltip>
                )}
              </div>
            ) : (
              <div></div>
            )}

            <KuikaButton
              data-testid="generateAppModalExportButton"
              data-onboarding-id="publish-popover-export-button"
              htmlType="submit"
              type="primary"
              disabled={
                this.state.activeTab === "Publish" &&
                (!this.state.selectedConfig || !this.state.selectedPublishTypes.length || isExportBlockedByBranch)
              }
              onClick={() => {
                if (this.state.activeTab === "Preview") {
                  this.props.handlePreviewButtonClick();
                  this.props.setShowPopup(false);
                }
                // htmlType="submit" already triggers form submission for Publish tab
              }}
            >
              {this.getActionButtonLabel()}
            </KuikaButton>
          </div>
        </div>
      </Form>
    );
  };

  render(): ReactNode {
    const shouldShowExportTooltip =
      this.props.previewStatus !== "idle" &&
      this.props.previewStatus !== undefined &&
      this.props.loadingSource !== "preview";

    const childrenWithTooltip = !this.props.showPopup ? (
      shouldShowExportTooltip ? (
        <KuikaTooltip
          content={this.getTooltipContent(this.props.previewStatus, this.props.loadingSource)}
          position="bottom"
          align="end"
          bgcolor={this.getTooltipColor(this.props.previewStatus)}
          open={true}
        >
          {this.props.children}
        </KuikaTooltip>
      ) : (
        <KuikaTooltip content="Run Settings">{this.props.children}</KuikaTooltip>
      )
    ) : (
      this.props.children
    );

    return (
      <>
        <KuikaPopover
          title={this.getPopupTitle()}
          content={this.getPopupContent()}
          visible={this.props.showPopup}
          onVisibleChange={this.handleVisibilityChange}
        >
          {childrenWithTooltip}
        </KuikaPopover>
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    showDomainAccessRequiredModal: (accessName) => dispatch(showDomainAccessRequiredModal(accessName)),
    setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
  };
};

const publishButton = connect(
  null,
  mapDispatchToProps
)(withContext<IPublishPopupButtonProps>(withSD<IPublishPopupButtonProps>(PublishButton)));
export { publishButton as PublishButton };
